import React, { useState, useEffect } from 'react';
import { 
  CheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Pesée } from '@/types';
import qualityAnalysisService from '@/services/qualityAnalysis';
import QualityAnalysisModal from '@/components/QualityAnalysisModal';
import { useCurrentWeighing } from '@/contexts/WeighingContext';
import { useWeighingRefresh } from '@/hooks/useWeighingRefresh';
import { RefoulementModal } from '@/components/modals';
import RefoulementTicket from '@/components/RefoulementTicket';
import refoulementService, { RefoulementData, RefoulementResponse, MotifRefoulement } from '@/services/refoulementService';
import { generateRefoulementPDF, printPDF, downloadPDF, emailPDF } from '@/services/pdfService';
import { useNavigate } from 'react-router-dom';
import PermissionGuard from '@/components/permissions/PermissionGuard';
import PermissionButton from '@/components/permissions/PermissionButton';
import { PERMISSIONS } from '@/types/permissions';

interface QualityPageProps {
  currentWeighing?: Pesée;
  onNextStep?: () => void;
  onPrevStep?: () => void;
}

const QualityPage: React.FC<QualityPageProps> = ({
  currentWeighing: propCurrentWeighing,
  onNextStep,
  onPrevStep
}) => {
  // Utiliser le contexte si pas de pesée passée en prop
  const contextCurrentWeighing = useCurrentWeighing();
  const currentWeighing = propCurrentWeighing || contextCurrentWeighing;
  const navigate = useNavigate();
  
  // Utiliser le hook de rechargement automatique
  const { } = useWeighingRefresh({
    weighingId: currentWeighing?.id?.toString(),
    autoRefresh: false, // Désactivé pour l'instant
    onRefresh: (weighing) => {
      console.log('🔄 Pesée rechargée automatiquement:', weighing);
    },
    onError: (error) => {
      console.error('❌ Erreur lors du rechargement:', error);
    }
  });
  const [formData, setFormData] = useState({
    lot_number: currentWeighing?.reference || 'BLE2024-015',
    grainnage: '', // Nouveau champ pour le grainnage (N/kg)
    humidite: 0.0, // Taux d'humidité en %
    quality: 0.0, // Qualité numérique
    quality_level: '', // Niveau de qualité (liste déroulante)
    observations: currentWeighing?.observations || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [qualityDecision, setQualityDecision] = useState<'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE' | ''>('');
  // Utiliser directement currentWeighing du contexte au lieu d'un état local
  // const [weighingDetails, setWeighingDetails] = useState<Pesée | null>(null);
  // const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // États pour la modale
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalQualityDecision, setModalQualityDecision] = useState<'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE' | undefined>();

  // États pour le refoulement
  const [showRefoulementModal, setShowRefoulementModal] = useState(false);
  const [showRefoulementTicket, setShowRefoulementTicket] = useState(false);
  const [refoulementData, setRefoulementData] = useState<RefoulementResponse | null>(null);
  const [isRefoulementLoading, setIsRefoulementLoading] = useState(false);

  // Fonctions pour gérer la modale
  const handleModalClose = () => {
    setShowModal(false);
    setModalMessage('');
    setModalQualityDecision(undefined);
  };

  const handleModalContinue = () => {
    handleModalClose();
    // Appeler la fonction onNextStep
    if (onNextStep) {
      onNextStep();
    }
  };

  const handleModalFinish = () => {
    handleModalClose();
    // Appeler la fonction onNextStep
    if (onNextStep) {
      onNextStep();
    }
  };

  // Fonctions pour gérer le refoulement
  const handleRefoulementClick = () => {
    if (!currentWeighing?.id) {
      setError('ID de la pesée non disponible');
      return;
    }
    setShowRefoulementModal(true);
  };

  const handleRefoulementConfirm = async (data: RefoulementData) => {
    if (!currentWeighing?.id) {
      setError('ID de la pesée non disponible');
      return;
    }

    setIsRefoulementLoading(true);
    setError('');

    try {
      const peseeId = parseInt(currentWeighing.id.toString());
      console.log('🔄 Création du refoulement pour la pesée:', peseeId);
      
      const refoulement = await refoulementService.creerRefoulement({
        ...data,
        pesee_id: peseeId
      });
      
      
      // Mettre à jour la décision qualité pour refléter le refoulement
      setQualityDecision('REFUSE');
      
      // Fermer la modale de refoulement
      setShowRefoulementModal(false);
      
      // Afficher le ticket de refoulement
      setRefoulementData(refoulement);
      setShowRefoulementTicket(true);
      
    } catch (err: any) {
      console.error('❌ Erreur lors de la création du refoulement:', err);
      setError(err.message || 'Erreur lors de la création du refoulement');
    } finally {
      setIsRefoulementLoading(false);
    }
  };

  const handleRefoulementTicketClose = () => {
    setShowRefoulementTicket(false);
    setRefoulementData(null);
    // Rediriger vers le dashboard
    navigate('/dashboard');
  };

  const handlePrintTicket = async () => {
    if (!refoulementData || !currentWeighing) return;
    
    try {
      const pdfData = {
        numero_refoulement: refoulementData.numero_refoulement,
        date_refoulement: refoulementData.date_refoulement,
        pesee_reference: refoulementData.pesee_reference,
        agent_refoulement: refoulementData.agent_refoulement_nom || refoulementData.agent_refoulement_username || 'N/A',
        motifs: convertMotifsForPDF(refoulementData.motifs),
        observations: refoulementData.observations,
        peseeData: {
          reference: currentWeighing.reference || '',
          grossWeight: Number(currentWeighing.poids_brut) || 0,
          tareWeight: Number(currentWeighing.poids_tare) || 0,
          netWeight: Number(currentWeighing.poids_net) || 0,
          vehicle: typeof currentWeighing.camion === 'string' ? currentWeighing.camion : (currentWeighing.camion as any)?.immatriculation || '',
          driver: typeof currentWeighing.chauffeur === 'string' ? currentWeighing.chauffeur : (currentWeighing.chauffeur as any)?.nom || '',
          product: typeof currentWeighing.produit === 'string' ? currentWeighing.produit : (currentWeighing.produit as any)?.nom || '',
          site: typeof currentWeighing.pont_bascule === 'string' ? currentWeighing.pont_bascule : (currentWeighing.pont_bascule as any)?.nom || '',
          grossAt: currentWeighing.heure_entree || '',
          tareAt: currentWeighing.heure_sortie || ''
        }
      };
      
      const doc = generateRefoulementPDF(pdfData);
      printPDF(doc);
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      setError('Erreur lors de l\'impression du ticket');
    }
  };

  const handleExportTicket = async () => {
    if (!refoulementData || !currentWeighing) return;

    try {
      const pdfData = {
        numero_refoulement: refoulementData.numero_refoulement,
        date_refoulement: refoulementData.date_refoulement,
        pesee_reference: refoulementData.pesee_reference,
        agent_refoulement: refoulementData.agent_refoulement_nom || refoulementData.agent_refoulement_username || 'N/A',
        motifs: convertMotifsForPDF(refoulementData.motifs),
        observations: refoulementData.observations,
        peseeData: {
          reference: currentWeighing.reference || '',
          grossWeight: Number(currentWeighing.poids_brut) || 0,
          tareWeight: Number(currentWeighing.poids_tare) || 0,
          netWeight: Number(currentWeighing.poids_net) || 0,
          vehicle: typeof currentWeighing.camion === 'string' ? currentWeighing.camion : (currentWeighing.camion as any)?.immatriculation || '',
          driver: typeof currentWeighing.chauffeur === 'string' ? currentWeighing.chauffeur : (currentWeighing.chauffeur as any)?.nom || '',
          product: typeof currentWeighing.produit === 'string' ? currentWeighing.produit : (currentWeighing.produit as any)?.nom || '',
          site: typeof currentWeighing.pont_bascule === 'string' ? currentWeighing.pont_bascule : (currentWeighing.pont_bascule as any)?.nom || '',
          grossAt: currentWeighing.heure_entree || '',
          tareAt: currentWeighing.heure_sortie || ''
        }
      };
      
      const doc = generateRefoulementPDF(pdfData);
      const filename = `refoulement_${refoulementData.numero_refoulement}.pdf`;
      downloadPDF(doc, filename);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      setError('Erreur lors de l\'export du ticket');
    }
  };

  const handleEmailTicket = async () => {
    if (!refoulementData || !currentWeighing) return;

    try {
      const pdfData = {
        numero_refoulement: refoulementData.numero_refoulement,
        date_refoulement: refoulementData.date_refoulement,
        pesee_reference: refoulementData.pesee_reference,
        agent_refoulement: refoulementData.agent_refoulement_nom || refoulementData.agent_refoulement_username || 'N/A',
        motifs: convertMotifsForPDF(refoulementData.motifs),
        observations: refoulementData.observations,
        peseeData: {
          reference: currentWeighing.reference || '',
          grossWeight: Number(currentWeighing.poids_brut) || 0,
          tareWeight: Number(currentWeighing.poids_tare) || 0,
          netWeight: Number(currentWeighing.poids_net) || 0,
          vehicle: typeof currentWeighing.camion === 'string' ? currentWeighing.camion : (currentWeighing.camion as any)?.immatriculation || '',
          driver: typeof currentWeighing.chauffeur === 'string' ? currentWeighing.chauffeur : (currentWeighing.chauffeur as any)?.nom || '',
          product: typeof currentWeighing.produit === 'string' ? currentWeighing.produit : (currentWeighing.produit as any)?.nom || '',
          site: typeof currentWeighing.pont_bascule === 'string' ? currentWeighing.pont_bascule : (currentWeighing.pont_bascule as any)?.nom || '',
          grossAt: currentWeighing.heure_entree || '',
          tareAt: currentWeighing.heure_sortie || ''
        }
      };
      
      const doc = generateRefoulementPDF(pdfData);
      const filename = `refoulement_${refoulementData.numero_refoulement}.pdf`;
      emailPDF(doc, filename);
    } catch (error) {
      console.error('Erreur lors de l\'envoi par email:', error);
      setError('Erreur lors de l\'envoi par email');
    }
  };

  // Fonction utilitaire pour convertir les motifs
  const convertMotifsForPDF = (motifs: MotifRefoulement[]) => {
    return motifs.map(motif => ({
      libelle: motif.libelle || motif.nom || 'Motif inconnu',
      description: motif.description || 'Description non disponible',
      categorie: motif.categorie || 'Non définie',
      criticite: motif.criticite || 'Non définie',
      code: motif.code
    }));
  };

  // La fonction fetchWeighingDetails n'est plus nécessaire car nous utilisons le contexte

  // Plus besoin de fetchWeighingDetails car nous utilisons directement le contexte

  // Mettre à jour les données du formulaire quand la pesée change
  useEffect(() => {
    if (currentWeighing) {
      setFormData(prev => ({
        ...prev,
        lot_number: currentWeighing.reference || prev.lot_number,
        observations: currentWeighing.observations || prev.observations
      }));
    }
  }, [currentWeighing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Validation en temps réel pour les champs critiques
    if (name === 'humidite') {
      const humiditeValue = parseFloat(value);
      if (humiditeValue > 10) {
        setValidationErrors(prev => ({
          ...prev,
          humidite: 'Humidité > 10% : Acceptation du lot impossible'
        }));
      }
    }
    
    if (name === 'quality') {
      const qualityValue = parseFloat(value);
      if (qualityValue < 45) {
        setValidationErrors(prev => ({
          ...prev,
          quality: 'Qualité < 45 : Acceptation sous réserve ou refoulement uniquement'
        }));
      }
    }
    
    // Réinitialiser la décision qualité si les conditions changent
    if (name === 'humidite' || name === 'quality' || name === 'quality_level') {
      setQualityDecision('');
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    // Validation des champs obligatoires
    if (!formData.grainnage || formData.grainnage.trim() === '') {
      errors.grainnage = 'Le grainnage est obligatoire';
    }
    
    if (!formData.humidite || formData.humidite <= 0) {
      errors.humidite = 'Le taux d\'humidité est obligatoire';
    }
    
    if (!formData.quality || formData.quality <= 0) {
      errors.quality = 'La qualité est obligatoire';
    }
    
    if (!formData.quality_level || formData.quality_level.trim() === '') {
      errors.quality_level = 'Le niveau de qualité est obligatoire';
    }
    
    if (!qualityDecision || qualityDecision.trim() === '') {
      errors.qualityDecision = 'La décision qualité est obligatoire';
    }
    
    // Contrôles de validation
    if (formData.humidite > 10) {
      errors.humidite = 'Humidité > 10% : Acceptation du lot impossible';
    }
    
    // Contrôles de validation pour la qualité
    if (formData.quality < 45) {
      errors.quality = 'Qualité < 45 : Acceptation sous réserve ou refoulement uniquement';
    }
    
    // Validation basée sur le niveau de qualité sélectionné
    if (formData.quality_level === 'FAIBLE' || formData.quality_level === 'TRES_FAIBLE') {
      // Pour les niveaux faibles, on peut ajouter une validation spécifique si nécessaire
    }
    
    // Vérifier que la référence est disponible (depuis currentWeighing)
    const reference = currentWeighing?.reference || formData.lot_number;
    if (!reference || reference.trim() === '') {
      errors.lot_number = 'La référence de la pesée est obligatoire';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      // S'assurer que l'ID de la pesée est disponible
      const peseeId = parseInt((currentWeighing?.id || '').toString());
      if (!peseeId || isNaN(peseeId)) {
        throw new Error('ID de la pesée non disponible');
      }

      console.log('🔬 Enregistrement de l\'analyse qualité pour la pesée:', peseeId);
      
      // Préparer les données pour l'API
      const analysisData = {
        grainnage: formData.grainnage,
        humidite: parseFloat(formData.humidite.toString()),
        quality: parseFloat(formData.quality.toString()),
        quality_level: formData.quality_level,
        quality_decision: qualityDecision as 'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE',
        observations: formData.observations
      };

    
      
      // Appeler le service d'API pour enregistrer l'analyse
      const response = await qualityAnalysisService.createQualityAnalysisForPesee(peseeId, analysisData);
      
      if (response.success) {
       
        // Afficher la modale de succès
        setModalType('success');
        setModalMessage('L\'analyse qualité a été enregistrée avec succès dans la base de données.');
        setModalQualityDecision(qualityDecision as 'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE');
        setShowModal(true);
      } else {
        
        // Afficher la modale d'erreur
        setModalType('error');
        setModalMessage(response.message);
        setShowModal(true);
      }
      
    } catch (err: any) {
      console.error('💥 Exception lors de l\'enregistrement:', err);
      
      // Afficher la modale d'erreur
      setModalType('error');
      setModalMessage(err.message || 'Erreur lors de l\'enregistrement de l\'analyse');
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Plus besoin de vérification de chargement car nous utilisons le contexte

  return (
    <PermissionGuard 
      permission={PERMISSIONS.QUALITY_ANALYSIS_VIEW}
      fallback={
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Accès refusé
            </h3>
            <p className="text-sm text-gray-500">
              Vous n'avez pas les permissions nécessaires pour accéder à l'analyse qualité.
            </p>
          </div>
        </div>
      }
    >
      <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Informations complètes de la pesée */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center mr-2">
              <i className="fas fa-info-circle text-blue-600 text-sm"></i>
            </div>
            <h3 className="text-base font-semibold text-blue-900">Informations de la pesée</h3>
          </div>
          <div className="bg-blue-100 px-3 py-1 rounded-md">
            <span className="text-sm font-mono font-semibold text-blue-800">
              #{currentWeighing?.reference || 'N/A'}
            </span>
          </div>
        </div>
        
        {/* Première ligne - Informations principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-white p-3 rounded-md border border-blue-100">
            <div className="flex items-center mb-1">
              <i className="fas fa-box text-orange-500 mr-1 text-sm"></i>
              <span className="text-xs font-medium text-gray-600">Produit</span>
            </div>
            <p className="font-semibold text-gray-900 text-sm">
              {currentWeighing?.produit_info?.nom || currentWeighing?.produit || 'Non spécifié'}
              {currentWeighing?.produit_info?.type_display && (
                <span className="text-orange-600 ml-1">({currentWeighing.produit_info.type_display})</span>
              )}
            </p>
          </div>
          
          <div className="bg-white p-3 rounded-md border border-blue-100">
            <div className="flex items-center mb-1">
              <i className="fas fa-truck text-green-500 mr-1 text-sm"></i>
              <span className="text-xs font-medium text-gray-600">Véhicule</span>
            </div>
            <p className="font-semibold text-gray-900 text-sm">
              {currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion || 'Non spécifié'}
              {currentWeighing?.camion_info?.marque && currentWeighing?.camion_info?.modele && (
                <span className="text-green-600 ml-1">({currentWeighing.camion_info.marque} {currentWeighing.camion_info.modele})</span>
              )}
            </p>
          </div>
          
          <div className="bg-white p-3 rounded-md border border-blue-100">
            <div className="flex items-center mb-1">
              <i className="fas fa-weight text-purple-500 mr-1 text-sm"></i>
              <span className="text-xs font-medium text-gray-600">Poids brut</span>
            </div>
            <p className="font-semibold text-gray-900 text-sm">
              {currentWeighing?.poids_brut ? 
                <span className="text-purple-600">{currentWeighing.poids_brut} kg</span> : 
                <span className="text-gray-400">Non pesé</span>
              }
            </p>
          </div>
          
          <div className="bg-white p-3 rounded-md border border-blue-100">
            <div className="flex items-center mb-1">
              <i className="fas fa-calendar text-indigo-500 mr-1 text-sm"></i>
              <span className="text-xs font-medium text-gray-600">Date pesée</span>
            </div>
            <p className="font-semibold text-gray-900 text-sm">
              {currentWeighing?.created_at ? 
                <span className="text-indigo-600">
                  {new Date(currentWeighing.created_at).toLocaleDateString('fr-FR')}
                </span> : 
                <span className="text-indigo-600">{new Date().toLocaleDateString('fr-FR')}</span>
              }
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Quality Criteria */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center mb-6">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
            <i className="fas fa-flask text-orange-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-orange-900">Analyse Qualité</h3>
        </div>
        
        <form id="quality-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Champ hidden pour la référence */}
          <input
            type="hidden"
            name="lot_number"
            value={currentWeighing?.reference || formData.lot_number}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Première ligne : Grainnage et Niveau de qualité */}
            <div className="bg-white p-4 rounded-lg border border-orange-100 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-seedling text-green-500 mr-2"></i>
                Grainnage (N/kg) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="grainnage"
                value={formData.grainnage}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  validationErrors.grainnage ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Ex: 180/200, 240/280, etc."
              />
              <p className="mt-1 text-xs text-gray-500">
                Format: nombre de grains par kilo (ex: 180/200)
              </p>
              {validationErrors.grainnage && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.grainnage}</p>
              )}
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-orange-100 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-star text-yellow-500 mr-2"></i>
                Niveau de qualité <span className="text-red-500">*</span>
              </label>
              <select
                name="quality_level"
                value={formData.quality_level}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  validationErrors.quality_level ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner le niveau</option>
                <option value="EXCELLENT">Excellent</option>
                <option value="BON">Bon</option>
                <option value="MOYEN">Moyen</option>
                <option value="FAIBLE">Faible</option>
                <option value="TRES_FAIBLE">Très faible</option>
              </select>
              {validationErrors.quality_level && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.quality_level}</p>
              )}
            </div>
            
            {/* Deuxième ligne : Taux d'humidité et Qualité */}
            <div className="bg-white p-4 rounded-lg border border-orange-100 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-tint text-blue-500 mr-2"></i>
                Taux d'humidité (%) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="humidite"
                  value={formData.humidite}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  max="100"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    validationErrors.humidite ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 8.5"
                />
                <span className="absolute right-3 top-3 text-gray-500 text-sm">Seuil: ≤10%</span>
              </div>
              {validationErrors.humidite && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.humidite}</p>
              )}
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-orange-100 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-star text-yellow-500 mr-2"></i>
                Qualité <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="quality"
                  value={formData.quality}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  max="100"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    validationErrors.quality ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 52.0"
                />
                <span className="absolute right-3 top-3 text-gray-500 text-sm">Min: ≥45</span>
              </div>
              {validationErrors.quality && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.quality}</p>
              )}
            </div>
            
          </div>
        </form>
      </div>

      {/* Évaluation automatique */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 mt-6 hidden">
        <h4 className="font-medium text-gray-900 mb-3">Évaluation automatique</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <span>Humidité: Limite</span>
          </div>
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
            <span>Protéines: Conforme</span>
          </div>
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
            <span>Poids HL: Conforme</span>
          </div>
        </div>
      </div>

      {/* Observations */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Observations</label>
        <textarea
          name="observations"
          value={formData.observations}
          onChange={handleInputChange}
          rows={4}
          placeholder="Remarques sur la qualité, défauts observés..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {/* Décision qualité */}
      <div className="border-t pt-6 mb-6">
        <h4 className="font-medium text-gray-900 mb-4">Décision qualité</h4>
        {/* Message d'alerte si conditions non respectées */}
        {(formData.humidite > 10 || formData.quality < 45) && (
          <div className="col-span-3 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Attention</h4>
                <div className="text-sm text-red-700 mt-1">
                  {formData.humidite > 10 && (
                    <p>• Humidité &gt; 10% : Acceptation du lot impossible</p>
                  )}
                  {formData.quality < 45 && (
                    <p>• Qualité &lt; 45 : Acceptation sous réserve ou refoulement uniquement</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Bouton Accepter - masqué si humidité > 10% OU qualité < 45 */}
          {formData.humidite <= 10 && formData.quality >= 45 && (
            <PermissionButton
              permission={PERMISSIONS.QUALITY_ANALYSIS_APPROVE}
              type="button"
              onClick={() => setQualityDecision('ACCEPTE')}
              className={`p-6 border-2 rounded-lg transition-colors text-center focus:outline-none focus:ring-2 ${
                qualityDecision === 'ACCEPTE' 
                  ? 'border-green-500 bg-green-100 text-green-900 ring-green-500' 
                  : 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-500'
              }`}
              disabledMessage="Permission insuffisante pour approuver"
            >
              <CheckIcon className="h-8 w-8 mb-2 mx-auto" />
              <p className="font-medium">Accepter</p>
              <p className="text-xs">Conforme aux standards</p>
            </PermissionButton>
          )}
          
          {/* Bouton Accepter avec réserve - toujours visible */}
          <PermissionButton
            permission={PERMISSIONS.QUALITY_ANALYSIS_APPROVE}
            type="button"
            onClick={() => setQualityDecision('ACCEPTE_RESERVE')}
            className={`p-6 border-2 rounded-lg transition-colors text-center focus:outline-none focus:ring-2 ${
              qualityDecision === 'ACCEPTE_RESERVE' 
                ? 'border-yellow-500 bg-yellow-100 text-yellow-900 ring-yellow-500' 
                : 'border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100 focus:ring-yellow-500'
            }`}
            disabledMessage="Permission insuffisante pour approuver"
          >
            <ExclamationTriangleIcon className="h-8 w-8 mb-2 mx-auto" />
            <p className="font-medium">Accepter avec réserve</p>
            <p className="text-xs">Qualité limite</p>
          </PermissionButton>
          
          {/* Bouton Refuser - toujours visible */}
          <PermissionButton
            permission={PERMISSIONS.QUALITY_ANALYSIS_REJECT}
            type="button"
            onClick={handleRefoulementClick}
            className={`p-6 border-2 rounded-lg transition-colors text-center focus:outline-none focus:ring-2 ${
              qualityDecision === 'REFUSE' 
                ? 'border-red-500 bg-red-100 text-red-900 ring-red-500' 
                : 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-500'
            }`}
            disabledMessage="Permission insuffisante pour rejeter"
          >
            <XMarkIcon className="h-8 w-8 mb-2 mx-auto" />
            <p className="font-medium">Refuser</p>
            <p className="text-xs">Non conforme</p>
          </PermissionButton>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <PermissionButton
          permission={PERMISSIONS.WEIGHING_EDIT}
          type="button"
          onClick={onPrevStep}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabledMessage="Permission insuffisante pour modifier la pesée"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Retour étape 1
        </PermissionButton>
        
        <div className="flex space-x-3">
          <PermissionButton
            permission={PERMISSIONS.WEIGHING_EDIT}
            type="button"
            onClick={() => {
              if (onNextStep) {
                onNextStep();
              }
            }}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabledMessage="Permission insuffisante pour ignorer l'étape"
          >
            <i className="fas fa-forward mr-2"></i>
            Ignorer l'étape
          </PermissionButton>
          
          <PermissionButton
            permission={PERMISSIONS.QUALITY_ANALYSIS_CREATE}
            type="submit"
            form="quality-form"
            disabled={isLoading}
            className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500"
            disabledMessage="Permission insuffisante pour créer une analyse qualité"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                Enregistrement...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-2 inline" />
                Valider l'analyse
              </>
            )}
          </PermissionButton>
        </div>
      </div>
      
      {/* Modale de succès/erreur */}
      <QualityAnalysisModal
        isOpen={showModal}
        onClose={handleModalClose}
        type={modalType}
        qualityDecision={modalQualityDecision}
        message={modalMessage}
        onContinue={modalType === 'success' ? handleModalContinue : undefined}
        onFinish={modalType === 'success' ? handleModalFinish : undefined}
      />

      {/* Modale de refoulement */}
      <RefoulementModal
        isOpen={showRefoulementModal}
        onClose={() => setShowRefoulementModal(false)}
        onConfirm={handleRefoulementConfirm}
        peseeId={currentWeighing?.id ? parseInt(currentWeighing.id.toString()) : 0}
        peseeReference={currentWeighing?.reference}
        peseeData={currentWeighing ? {
          reference: currentWeighing.reference || '',
          grossWeight: Number(currentWeighing.poids_brut) || 0,
          tareWeight: Number(currentWeighing.poids_tare) || 0,
          netWeight: Number(currentWeighing.poids_net) || 0,
          vehicle: typeof currentWeighing.camion === 'string' ? currentWeighing.camion : (currentWeighing.camion as any)?.immatriculation || '',
          driver: typeof currentWeighing.chauffeur === 'string' ? currentWeighing.chauffeur : (currentWeighing.chauffeur as any)?.nom || '',
          product: typeof currentWeighing.produit === 'string' ? currentWeighing.produit : (currentWeighing.produit as any)?.nom || '',
          site: typeof currentWeighing.pont_bascule === 'string' ? currentWeighing.pont_bascule : (currentWeighing.pont_bascule as any)?.nom || '',
          tenant: typeof currentWeighing.pont_bascule === 'string' ? '' : (currentWeighing.pont_bascule as any)?.site?.nom || '',
          grossAt: currentWeighing.heure_entree || '',
          tareAt: currentWeighing.heure_sortie || '',
          grossUser: typeof currentWeighing.agent_premiere_pesee === 'string' ? currentWeighing.agent_premiere_pesee : (currentWeighing.agent_premiere_pesee as any)?.username || '',
          tareUser: typeof currentWeighing.agent_deuxieme_pesee === 'string' ? currentWeighing.agent_deuxieme_pesee : (currentWeighing.agent_deuxieme_pesee as any)?.username || ''
        } : undefined}
        isLoading={isRefoulementLoading}
      />

      {/* Ticket de refoulement */}
      {showRefoulementTicket && refoulementData && currentWeighing && (
        <RefoulementTicket
          refoulementData={refoulementData}
          peseeData={{
            reference: currentWeighing.reference || '',
            grossWeight: Number(currentWeighing.poids_brut) || 0,
            tareWeight: Number(currentWeighing.poids_tare) || 0,
            netWeight: Number(currentWeighing.poids_net) || 0,
            vehicle: typeof currentWeighing.camion === 'string' ? currentWeighing.camion : (currentWeighing.camion as any)?.immatriculation || '',
            driver: typeof currentWeighing.chauffeur === 'string' ? currentWeighing.chauffeur : (currentWeighing.chauffeur as any)?.nom || '',
            product: typeof currentWeighing.produit === 'string' ? currentWeighing.produit : (currentWeighing.produit as any)?.nom || '',
            site: typeof currentWeighing.pont_bascule === 'string' ? currentWeighing.pont_bascule : (currentWeighing.pont_bascule as any)?.nom || '',
            tenant: typeof currentWeighing.pont_bascule === 'string' ? '' : (currentWeighing.pont_bascule as any)?.site?.nom || '',
            grossAt: currentWeighing.heure_entree || '',
            tareAt: currentWeighing.heure_sortie || '',
            grossUser: typeof currentWeighing.agent_premiere_pesee === 'string' ? currentWeighing.agent_premiere_pesee : (currentWeighing.agent_premiere_pesee as any)?.username || '',
            tareUser: typeof currentWeighing.agent_deuxieme_pesee === 'string' ? currentWeighing.agent_deuxieme_pesee : (currentWeighing.agent_deuxieme_pesee as any)?.username || ''
          }}
          onClose={handleRefoulementTicketClose}
          onPrint={handlePrintTicket}
          onExport={handleExportTicket}
          onEmail={handleEmailTicket}
        />
      )}
      </div>
    </PermissionGuard>
  );
};

export default QualityPage;
